import React, { useState, useEffect } from 'react';
import db from '../../utils/database';
import { useNotification } from '../../context/NotificationContext';
import { handleDatabaseError } from '../../utils/errorHandler';
import LoadingIndicator from '../../components/common/LoadingIndicator';

const SchemaViewer = ({ tableName, password }) => {
  const notification = useNotification();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [structure, setStructure] = useState([]);
  const [indexes, setIndexes] = useState([]);
  const [foreignKeys, setForeignKeys] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    columns: true,
    indexes: false,
    foreignKeys: false,
    ddl: false,
  });
  
  // Load schema data when component mounts or when table changes
  useEffect(() => {
    if (tableName) {
      loadTableSchema();
    }
  }, [tableName]);
  
  // Load table schema information
  const loadTableSchema = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get table structure (columns)
      const structureResult = await db.getTableStructure(tableName);
      setStructure(structureResult);
      
      // Get table indexes
      const indexesQuery = `
        SELECT 
          name AS index_name,
          "unique" AS is_unique,
          partial AS is_partial,
          origin AS index_type
        FROM pragma_index_list('${tableName}')`;
      const indexesResult = await db.query(indexesQuery);
      
      // Get index columns
      const indexData = await Promise.all(
        indexesResult.rows.map(async (index) => {
          const columnsQuery = `
            SELECT
              name AS column_name,
              seqno AS sequence
            FROM pragma_index_info('${index.index_name}')`;
          const columnsResult = await db.query(columnsQuery);
          
          return {
            ...index,
            columns: columnsResult.rows,
          };
        })
      );
      
      setIndexes(indexData);
      
      // Get foreign keys
      const foreignKeysQuery = `
        SELECT 
          id,
          seq,
          "table" AS referenced_table,
          "from" AS column_name,
          "to" AS referenced_column,
          on_update,
          on_delete,
          match AS match_option
        FROM pragma_foreign_key_list('${tableName}')`;
      const foreignKeysResult = await db.query(foreignKeysQuery);
      
      // Group foreign keys by ID
      const groupedForeignKeys = {};
      for (const fk of foreignKeysResult.rows) {
        if (!groupedForeignKeys[fk.id]) {
          groupedForeignKeys[fk.id] = {
            id: fk.id,
            referenced_table: fk.referenced_table,
            on_update: fk.on_update,
            on_delete: fk.on_delete,
            match_option: fk.match_option,
            columns: [],
          };
        }
        
        groupedForeignKeys[fk.id].columns.push({
          column_name: fk.column_name,
          referenced_column: fk.referenced_column,
          sequence: fk.seq,
        });
      }
      
      setForeignKeys(Object.values(groupedForeignKeys));

      
    } catch (err) {
      const errorMessage = handleDatabaseError(err, 'schema loading');
      setError(errorMessage);
      notification.showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Generate CREATE TABLE statement
  const generateCreateTableSQL = () => {
    if (!structure || structure.length === 0) {
      return '';
    }
    
    let sql = `CREATE TABLE "${tableName}" (\n`;
    
    // Add columns
    const columns = structure.map((column) => {
      let columnDef = `  "${column.name}" ${column.type}`;
      
      if (column.notnull) {
        columnDef += ' NOT NULL';
      }
      
      if (column.dflt_value !== null) {
        columnDef += ` DEFAULT ${column.dflt_value}`;
      }
      
      if (column.pk) {
        columnDef += ' PRIMARY KEY';
        
        if (column.autoincrement) {
          columnDef += ' AUTOINCREMENT';
        }
      }
      
      return columnDef;
    });
    
    // Add foreign key constraints
    const fkConstraints = foreignKeys.map((fk) => {
      const columnNames = fk.columns.map((col) => `"${col.column_name}"`).join(', ');
      const refColumnNames = fk.columns.map((col) => `"${col.referenced_column}"`).join(', ');
      
      let constraint = `  FOREIGN KEY (${columnNames}) REFERENCES "${fk.referenced_table}" (${refColumnNames})`;
      
      if (fk.on_delete !== 'NO ACTION') {
        constraint += ` ON DELETE ${fk.on_delete}`;
      }
      
      if (fk.on_update !== 'NO ACTION') {
        constraint += ` ON UPDATE ${fk.on_update}`;
      }
      
      return constraint;
    });
    
    // Combine all parts of the CREATE TABLE statement
    sql += [...columns, ...fkConstraints].join(',\n');
    sql += '\n);';
    
    // Add index creation statements
    indexes.forEach((index) => {
      if (index.index_name.startsWith(`sqlite_autoindex_${tableName}`)) {
        return; // Skip auto-generated indexes
      }
      
      const columnDefs = index.columns.map((col) => {
        return `"${col.column_name}"`;
      }).join(', ');
      
      sql += `\n\nCREATE ${index.is_unique ? 'UNIQUE ' : ''}INDEX "${index.index_name}" ON "${tableName}" (${columnDefs});`;
    });
    
    return sql;
  };
  
  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };
  
  // Get SQLite type for display
  const getDisplayType = (sqliteType) => {
    // Normalize SQLite type (remove size/precision)
    const baseType = sqliteType.split('(')[0].toUpperCase();
    
    // Map of SQLite types to display colors
    const typeColors = {
      INTEGER: 'bg-blue-100 text-blue-800',
      REAL: 'bg-green-100 text-green-800',
      TEXT: 'bg-purple-100 text-purple-800',
      BLOB: 'bg-yellow-100 text-yellow-800',
      NUMERIC: 'bg-indigo-100 text-indigo-800',
      BOOLEAN: 'bg-orange-100 text-orange-800',
      DATE: 'bg-pink-100 text-pink-800',
      DATETIME: 'bg-pink-100 text-pink-800',
      'TIMESTAMP': 'bg-pink-100 text-pink-800',
    };
    
    return {
      type: baseType,
      color: typeColors[baseType] || 'bg-gray-100 text-gray-800',
    };
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingIndicator size="large" text="Loading table schema..." />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }
  
  if (!tableName) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a table to view structure.</p>
      </div>
    );
  }
  
  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Table Structure: {tableName}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          View detailed schema information for this table.
        </p>
      </div>
      
      <div className="divide-y divide-gray-200">
        {/* Columns Section */}
        <div className="border-b border-gray-200">
          <button
            className="w-full px-4 py-4 text-left focus:outline-none flex items-center justify-between"
            onClick={() => toggleSection('columns')}
          >
            <div className="flex items-center">
              <svg
                className={`h-5 w-5 text-gray-500 mr-2 transform ${expandedSections.columns ? 'rotate-90' : 'rotate-0'}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="font-medium text-gray-700">Table Columns</span>
            </div>
            <span className="text-sm text-gray-500">{structure.length} columns</span>
          </button>
          
          {expandedSections.columns && (
            <div className="px-4 py-4 bg-gray-50">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        #
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Attributes
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Default
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {structure.map((column, index) => {
                      const typeInfo = getDisplayType(column.type);
                      
                      return (
                        <tr key={column.name} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {index + 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="text-sm font-medium text-gray-900">
                                {column.name}
                              </div>
                              {column.pk && (
                                <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                  PK
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${typeInfo.color}`}>
                              {column.type}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex flex-wrap gap-1">
                              {column.notnull && (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                  NOT NULL
                                </span>
                              )}
                              {column.pk && column.autoincrement && (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                  AUTO INC
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {column.dflt_value !== null ? column.dflt_value : 'NULL'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        
        {/* Indexes Section */}
        <div className="border-b border-gray-200">
          <button
            className="w-full px-4 py-4 text-left focus:outline-none flex items-center justify-between"
            onClick={() => toggleSection('indexes')}
          >
            <div className="flex items-center">
              <svg
                className={`h-5 w-5 text-gray-500 mr-2 transform ${expandedSections.indexes ? 'rotate-90' : 'rotate-0'}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="font-medium text-gray-700">Indexes</span>
            </div>
            <span className="text-sm text-gray-500">{indexes.length} indexes</span>
          </button>
          
          {expandedSections.indexes && (
            <div className="px-4 py-4 bg-gray-50">
              {indexes.length === 0 ? (
                <p className="text-gray-500 text-sm">No indexes defined for this table.</p>
              ) : (
                <div className="space-y-4">
                  {indexes.map((index) => (
                    <div key={index.index_name} className="bg-white shadow overflow-hidden rounded-md">
                      <div className="px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center">
                          <svg className="h-5 w-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 11l5-5m0 0l5 5m-5-5v12" />
                          </svg>
                          <span className="font-medium text-gray-700">{index.index_name}</span>
                        </div>
                        <div className="flex space-x-2">
                          {index.is_unique && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-100 text-indigo-800">
                              UNIQUE
                            </span>
                          )}
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            {index.index_type}
                          </span>
                        </div>
                      </div>
                      <div className="px-4 py-2 border-t border-gray-200">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                          Columns
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {index.columns.map((column) => (
                            <span key={column.column_name} className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                              {column.column_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Foreign Keys Section */}
        <div className="border-b border-gray-200">
          <button
            className="w-full px-4 py-4 text-left focus:outline-none flex items-center justify-between"
            onClick={() => toggleSection('foreignKeys')}
          >
            <div className="flex items-center">
              <svg
                className={`h-5 w-5 text-gray-500 mr-2 transform ${expandedSections.foreignKeys ? 'rotate-90' : 'rotate-0'}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="font-medium text-gray-700">Foreign Keys</span>
            </div>
            <span className="text-sm text-gray-500">{foreignKeys.length} foreign keys</span>
          </button>
          
          {expandedSections.foreignKeys && (
            <div className="px-4 py-4 bg-gray-50">
              {foreignKeys.length === 0 ? (
                <p className="text-gray-500 text-sm">No foreign keys defined for this table.</p>
              ) : (
                <div className="space-y-4">
                  {foreignKeys.map((fk) => (
                    <div key={fk.id} className="bg-white shadow overflow-hidden rounded-md">
                      <div className="px-4 py-3">
                        <div className="flex items-center">
                          <svg className="h-5 w-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.172 13.828a4 4 0 015.656 0l4 4a4 4 0 01-5.656 5.656l-1.102-1.101" />
                          </svg>
                          <span className="font-medium text-gray-700">
                            References <span className="text-indigo-600">{fk.referenced_table}</span>
                          </span>
                        </div>
                      </div>
                      <div className="px-4 py-2 border-t border-gray-200">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                          Column Mapping
                        </div>
                        <div className="space-y-1">
                          {fk.columns.map((col) => (
                            <div key={col.column_name} className="flex items-center">
                              <span className="font-medium text-gray-800">{col.column_name}</span>
                              <svg className="h-4 w-4 text-gray-400 mx-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                              </svg>
                              <span className="text-gray-800">{col.referenced_column}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="px-4 py-2 border-t border-gray-200 grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                            On Delete
                          </div>
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            fk.on_delete === 'CASCADE' ? 'bg-red-100 text-red-800' :
                            fk.on_delete === 'SET NULL' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {fk.on_delete}
                          </span>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                            On Update
                          </div>
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            fk.on_update === 'CASCADE' ? 'bg-blue-100 text-blue-800' :
                            fk.on_update === 'SET NULL' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {fk.on_update}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* DDL Section */}
        <div>
          <button
            className="w-full px-4 py-4 text-left focus:outline-none flex items-center justify-between"
            onClick={() => toggleSection('ddl')}
          >
            <div className="flex items-center">
              <svg
                className={`h-5 w-5 text-gray-500 mr-2 transform ${expandedSections.ddl ? 'rotate-90' : 'rotate-0'}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="font-medium text-gray-700">SQL</span>
            </div>
            <span className="text-sm text-gray-500">CREATE TABLE</span>
          </button>
          
          {expandedSections.ddl && (
            <div className="px-4 py-4 bg-gray-50">
              <div className="flex justify-end mb-2">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(generateCreateTableSQL());
                  }}
                  className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Copy SQL
                </button>
              </div>
              <div className="bg-gray-800 rounded-md overflow-x-auto">
                <pre className="text-sm text-gray-200 p-4 whitespace-pre overflow-x-auto">
                  {generateCreateTableSQL()}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchemaViewer;